/**
 * Remote $ref support — discovery + fetch.
 *
 * The schema engine resolver consults a `RefRegistry` (Map<url, schema>)
 * for `http(s)://...` refs. The web app populates that registry by
 * scanning the root schema for remote ref URLs and fetching each one.
 *
 * Fetching happens in the browser, so the URL must serve appropriate
 * CORS headers. A failed fetch leaves the URL absent from the registry;
 * the resolver then surfaces a "schema not loaded" message rather than
 * crashing. The host app can show the fetch errors as a banner.
 *
 * No persistence layer here — refetch on each project open. Browser HTTP
 * caching does the right thing for the common case (static schema files
 * with cache headers).
 */

import type { JsonSchema } from '@bliksys/shared';

/**
 * Walk a schema (recursively, following all keyword sub-schemas) and
 * collect every distinct `http(s)://` URL referenced via `$ref`. The
 * fragment is stripped — we only return the URL part, since that's
 * what the registry is keyed by.
 */
export function collectRemoteRefs(schema: JsonSchema): string[] {
  const seen = new Set<string>();
  walk(schema, seen);
  return [...seen];
}

function walk(node: unknown, seen: Set<string>): void {
  if (node === null || typeof node !== 'object') return;
  if (Array.isArray(node)) {
    for (const item of node) walk(item, seen);
    return;
  }
  const obj = node as Record<string, unknown>;
  // Capture this node's $ref if present and remote.
  const ref = obj.$ref;
  if (typeof ref === 'string' && (ref.startsWith('http://') || ref.startsWith('https://'))) {
    const hashIdx = ref.indexOf('#');
    const url = hashIdx === -1 ? ref : ref.slice(0, hashIdx);
    seen.add(url);
  }
  // Recurse into all object/array children. We don't try to be selective
  // about which keywords are sub-schemas — covering them all (properties,
  // items, $defs, oneOf, etc.) without enumerating each is simpler and
  // correctly handles future-additions.
  for (const v of Object.values(obj)) walk(v, seen);
}

/**
 * Fetch a single remote schema URL and parse it as JSON. Returns the
 * parsed schema or throws with a useful message. The caller decides
 * whether to surface the error as a banner or just stash it.
 */
export async function fetchRemoteSchema(url: string): Promise<JsonSchema> {
  let res: Response;
  try {
    res = await fetch(url, { headers: { Accept: 'application/json,application/schema+json' } });
  } catch (err) {
    throw new Error(`Network error fetching ${url}: ${(err as Error).message}`);
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText} fetching ${url}`);
  }
  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch (err) {
    throw new Error(`${url} did not return valid JSON: ${(err as Error).message}`);
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error(`${url} did not return a JSON object`);
  }
  return parsed;
}

/**
 * Fetch all remote refs in a schema in parallel. Returns a registry
 * (URL → schema) AND a map of per-URL errors for ones that failed.
 * Both maps are partial — a successful URL appears in `registry` and
 * not in `errors`, and vice versa.
 */
export async function fetchAllRemoteRefs(schema: JsonSchema): Promise<{
  registry: Map<string, JsonSchema>;
  errors: Map<string, string>;
}> {
  const urls = collectRemoteRefs(schema);
  const registry = new Map<string, JsonSchema>();
  const errors = new Map<string, string>();
  await Promise.all(
    urls.map(async (url) => {
      try {
        const fetched = await fetchRemoteSchema(url);
        registry.set(url, fetched);
      } catch (err) {
        errors.set(url, (err as Error).message);
      }
    }),
  );
  return { registry, errors };
}
